import { ChangeDetectionStrategy, Component, inject, OnInit, input } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  selectRankingColumns,
  selectRankingHeader,
  selectRankingLoader,
  selectRankingRows,
  selectShowFooter,
  selectWarningI18n
} from '@features/rankings/store/selectors/ranking.selectors'
import { closeRankingModal, refreshRanking } from '@features/rankings/store/actions/ranking.actions'
import { RankedMetric, RankingHeader } from '@features/rankings/models/ranking.model'
import { SegmentModifier } from '@shared/components/segmented-control/segmented-control.models'
import { RANKING_ROW_STYLES } from '@features/rankings/constants/rankings.constants'
import { ContextMenuEntity, ContextMenuHolderWrapper } from '@core/models/context-menu.models'
import { excludeNegativeRankingFromSortFn } from '@features/rankings/utils/ranking.utils'
import {
  selectAggregationModesToRank,
  selectAggregationToRank
} from '@features/aggregations/store/selectors/aggregations.selectors'
import { Observable } from 'rxjs'
import { Aggregation, AggregationSelectable } from '@features/aggregations/models/aggregations.models'
import { CallingContext } from '@features/seasons/models/seasons.models'
import { RankingHeaderComponent } from './components/ranking-header/ranking-header.component'
import { SegmentedControlComponent } from '@shared/components/segmented-control/segmented-control.component'
import { LoaderComponent } from '@shared/components/loader/loader.component'
import { TableComponent } from '@shared/components/table/table.component'
import { DefaultImageModule } from '@mediacoach/ui'
import { TranslatePipe } from '@ngx-translate/core'
import { AsyncPipe } from '@angular/common'
import { InvalidValueHandlerPipe } from '@shared/pipes/invalid-value-handler.pipe'
import { IonIcon } from '@ionic/angular/standalone'

@Component({
  selector: 'mcm-ranking-container',
  templateUrl: './ranking-container.component.html',
  styleUrls: ['./ranking-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RankingHeaderComponent,
    SegmentedControlComponent,
    LoaderComponent,
    TableComponent,
    DefaultImageModule,
    TranslatePipe,
    AsyncPipe,
    InvalidValueHandlerPipe,
    IonIcon
  ]
})
export class RankingContainerComponent implements OnInit {
  private readonly _store: Store = inject(Store)

  readonly excludeFromSortFn = excludeNegativeRankingFromSortFn

  readonly loading$ = this._store.select(selectRankingLoader)
  readonly warning$ = this._store.select(selectWarningI18n)
  readonly columns$ = this._store.select(selectRankingColumns)
  readonly aggregationModes$: Observable<AggregationSelectable[]> = this._store.select(selectAggregationModesToRank)
  readonly aggregationMode$ = this._store.select(selectAggregationToRank)
  readonly showFooter$ = this._store.select(selectShowFooter)

  rows$!: Observable<RankedMetric[]>
  header$!: Observable<RankingHeader>

  readonly segmentModifiers = [SegmentModifier.SecondLevel, SegmentModifier.NoMargin] as SegmentModifier[]
  readonly rowStyles = RANKING_ROW_STYLES

  readonly callingContext = input.required<CallingContext>()

  metricContext: ContextMenuEntity

  get overrideAggregationMode() {
    return (this.metricContext as ContextMenuHolderWrapper)?.overrideAggregationMode
  }

  ngOnInit(): void {
    this.rows$ = this._store.select(selectRankingRows(this.callingContext()))
    this.header$ = this._store.select(selectRankingHeader(this.callingContext()))
  }

  close() {
    this._store.dispatch(closeRankingModal())
  }

  trackByFn(index: number, rank: RankedMetric): string {
    return rank.id
  }

  onModeChange(value: string) {
    this._store.dispatch(refreshRanking({ aggregation: value as Aggregation }))
  }
}
